import React from "react";

/**
 * Hero illustration — a hand-drawn technical architecture diagram of the stack
 * this portfolio is about. Three inputs (client apps, cloud, AI/RAG) feed one
 * backend tier, which in turn feeds data + infrastructure.
 *
 * Drawn on a 460 x 400 grid. Every colour comes from the .art-* classes in
 * index.css, so the palette lives in exactly one place.
 *
 * Layout is anchored from a single vertical axis (x = 230) and every card lays
 * its content out on the same offsets from its own top edge: icon +18,
 * title +52, sub-label +72 / +84.
 *
 * Geometry note: the animated flow line rides the central spine of the routed
 * connectors, so the pulse always tracks a real wire.
 */

const FLOW_PATH = "M 230 100 L 230 336";

const HeroArt = () => (
  <svg
    viewBox="0 0 460 400"
    role="img"
    aria-label="Architecture diagram: client apps, Oracle Cloud and AI/RAG feed into backend services, which feed databases and infrastructure"
  >
    <defs>
      <filter id="artShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow
          dx="0"
          dy="2"
          stdDeviation="3"
          floodColor="#17171B"
          floodOpacity="0.05"
        />
      </filter>
      <radialGradient id="artGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="var(--accent-tint)" stopOpacity="0.55" />
        <stop offset="100%" stopColor="var(--accent-tint)" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* ── Background glow ── */}
    <ellipse cx="230" cy="230" rx="220" ry="112" fill="url(#artGlow)" />

    {/* ── Routed connectors ── */}
    <g className="art-line">
      {/* top tier → backend */}
      <path d="M 230 128 L 230 184" />
      <path d="M 78 128 L 78 148 Q 78 156 86 156 L 230 156" />
      <path d="M 382 128 L 382 148 Q 382 156 374 156 L 230 156" />
      {/* backend → bottom tier */}
      <path d="M 230 276 L 230 291" />
      <path d="M 112 306 L 112 299 Q 112 291 120 291 L 230 291" />
      <path d="M 348 306 L 348 299 Q 348 291 340 291 L 230 291" />
    </g>
    <circle className="art-bar-dot" cx="230" cy="156" r="2.5" />
    <circle className="art-bar-dot" cx="230" cy="291" r="2.5" />

    {/* ── Animated flow pulse along the central spine ── */}
    <path className="art-flow" d={FLOW_PATH} />

    {/* ══ Top tier ══════════════════════════════════════════════ */}

    {/* Client Apps */}
    <rect
      className="art-plate"
      x="10"
      y="20"
      width="136"
      height="108"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(30, 38)">
      <rect className="art-plate" x="0" y="1" width="16" height="12" rx="2.5" />
      <rect className="art-plate" x="6" y="8" width="16" height="12" rx="2.5" />
      <line className="art-line" x1="6" y1="12" x2="22" y2="12" />
    </g>
    <text className="art-title" x="30" y="72">
      Client Apps
    </text>
    <text className="art-sub" x="30" y="92">
      WEB · MOBILE · API
    </text>

    {/* Oracle Cloud */}
    <rect
      className="art-plate"
      x="162"
      y="20"
      width="136"
      height="108"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(182, 40)">
      <path
        className="art-plate"
        d="M 4 16 Q -1 16 1 11 Q 1 6 7 7 Q 8 1 15 2 Q 21 2 20 8 Q 24 9 21 13 Q 20 16 15 16 Z"
      />
    </g>
    <text className="art-title" x="182" y="72">
      Oracle Cloud
    </text>
    <text className="art-sub" x="182" y="92">
      OCI
    </text>

    {/* AI / RAG */}
    <rect
      className="art-plate"
      x="314"
      y="20"
      width="136"
      height="108"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(334, 38)">
      <line className="art-line" x1="3" y1="18" x2="19" y2="18" />
      <line className="art-line" x1="3" y1="18" x2="11" y2="4" />
      <line className="art-line" x1="19" y1="18" x2="11" y2="4" />
      <circle className="art-plate" cx="3" cy="18" r="3" />
      <circle className="art-plate" cx="19" cy="18" r="3" />
      <circle className="art-node-accent" cx="11" cy="4" r="3" />
    </g>
    <text className="art-title" x="334" y="72">
      AI / RAG
    </text>
    <text className="art-sub" x="334" y="92">
      RETRIEVAL · GENERATION
    </text>
    <text className="art-sub" x="334" y="104">
      AI SYSTEMS
    </text>

    {/* ══ Backend tier ══════════════════════════════════════════ */}

    <rect
      className="art-plate-tint"
      x="60"
      y="184"
      width="340"
      height="92"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(80, 202)">
      <rect className="art-plate" x="1" y="2" width="20" height="5" rx="1.5" />
      <rect
        className="art-plate-tint"
        x="1"
        y="9.5"
        width="20"
        height="5"
        rx="1.5"
      />
      <rect className="art-plate" x="1" y="17" width="20" height="5" rx="1.5" />
    </g>
    <text className="art-title" x="80" y="236">
      Backend Services
    </text>
    <text className="art-sub" x="80" y="256">
      JAVA · DROPWIZARD · MICROSERVICES
    </text>
    <text className="art-sub" x="80" y="268">
      EVENT DRIVEN
    </text>

    {/* ══ Bottom tier ═══════════════════════════════════════════ */}

    {/* Databases */}
    <rect
      className="art-plate"
      x="10"
      y="306"
      width="206"
      height="88"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(30, 324)">
      <path className="art-line" d="M 3 5 V 17 A 8 3 0 0 0 19 17 V 5" />
      <ellipse className="art-plate" cx="11" cy="5" rx="8" ry="3" />
      <path className="art-line" d="M 3 11 A 8 3 0 0 0 19 11" />
    </g>
    <text className="art-title" x="30" y="358">
      Databases
    </text>
    <text className="art-sub" x="30" y="378">
      ORACLE DB · NOSQL
    </text>

    {/* Infrastructure */}
    <rect
      className="art-plate"
      x="244"
      y="306"
      width="206"
      height="88"
      rx="16"
      filter="url(#artShadow)"
    />
    <g transform="translate(264, 324)">
      <rect className="art-plate" x="0" y="9" width="10" height="10" rx="1.5" />
      <rect className="art-plate" x="12" y="9" width="10" height="10" rx="1.5" />
      <rect className="art-plate" x="6" y="1" width="10" height="10" rx="1.5" />
    </g>
    <text className="art-title" x="264" y="358">
      Infrastructure
    </text>
    <text className="art-sub" x="264" y="378">
      TERRAFORM · DOCKER · KUBERNETES
    </text>
  </svg>
);

export default HeroArt;
